import axios from 'axios';
import CryptoJS from 'crypto-js';

// ERP 签名密钥与 base URL
const ERP_SECRET = '7kS9p2rD8tG5bQ4nZ6cJ0mX3vR1hF7wL9uP2sA5dN8qB';
const ERP_API_BASE = process.env.NODE_ENV === 'development'
  ? '/erp-dialog'
  : (process.env.VUE_APP_ERP_API_BASE || 'https://shltyy.jsltpct.com:9526/shltyy');

/**
 * 生成 ERP 创建会话接口所需的 HMAC-SHA256 签名
 * 规则：payload = nonce + timestamp + openid
 */
async function generateErpSignature(openid, secret) {
  const timestamp = Date.now().toString();
  const nonce = Math.random().toString(36).substring(2, 18);
  const payload = nonce + timestamp + openid;
  const signature = CryptoJS.HmacSHA256(payload, secret).toString(CryptoJS.enc.Hex);
  return { timestamp, nonce, signature };
}

/**
 * ERP 创建会话（该接口会复用同一 openid 下仍在进行中的会话）
 */
async function createErpSession(params) {
  const openid = params.openid;
  if (!openid) {
    throw new Error('缺少 ERP openid');
  }
  const { timestamp, nonce, signature } = await generateErpSignature(openid, ERP_SECRET);
  const body = new URLSearchParams();
  body.append('openid', openid);
  body.append('timestamp', timestamp);
  body.append('nonce', nonce);
  body.append('signature', signature);
  if (params.driverType !== undefined) {
    body.append('driverType', params.driverType);
  }
  if (params.templateCode !== undefined) {
    body.append('templateCode', params.templateCode);
  }
  if (params.jobType !== undefined && params.jobType !== null) {
    body.append('jobType', String(params.jobType));
  }
  if (params.clientInfo) {
    body.append('clientInfo', params.clientInfo);
  }
  if (params.force) {
    body.append('force', 'true');
  }
  const res = await axios.post(`${ERP_API_BASE}/dialog/session/create/erp`, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    timeout: 60000,
  });
  return res.data;
}

function buildErpSignedQuery(openid, extraParams = {}) {
  const timestamp = Date.now().toString();
  const nonce = Math.random().toString(36).substring(2, 18);
  const payload = nonce + timestamp + openid;
  const signature = CryptoJS.HmacSHA256(payload, ERP_SECRET).toString(CryptoJS.enc.Hex);
  const query = new URLSearchParams();
  query.append('openid', openid);
  query.append('timestamp', timestamp);
  query.append('nonce', nonce);
  query.append('signature', signature);
  Object.keys(extraParams).forEach((key) => {
    if (extraParams[key] !== undefined && extraParams[key] !== null) {
      query.append(key, String(extraParams[key]));
    }
  });
  return query.toString();
}

export const erpDialogApi = {
  createErpSession,
  getSession(sessionId, openid) {
    if (!openid) return Promise.reject(new Error('缺少 openid'));
    const query = buildErpSignedQuery(openid);
    return axios.get(`${ERP_API_BASE}/dialog/session/erp/${sessionId}?${query}`, { timeout: 60000 }).then((r) => r.data);
  },
  getHint(sessionId, openid) {
    if (!openid) return Promise.reject(new Error('缺少 openid'));
    const query = buildErpSignedQuery(openid);
    return axios.get(`${ERP_API_BASE}/dialog/session/erp/${sessionId}/hint?${query}`, { timeout: 60000 }).then((r) => r.data);
  },
  getMessages(sessionId, openid, limit = 50) {
    if (!openid) return Promise.reject(new Error('缺少 openid'));
    const query = buildErpSignedQuery(openid, { limit });
    return axios.get(`${ERP_API_BASE}/dialog/session/erp/${sessionId}/messages?${query}`, { timeout: 60000 }).then((r) => r.data);
  },
  getReviewStatus(sessionId, openid) {
    if (!openid) return Promise.reject(new Error('缺少 openid'));
    const query = buildErpSignedQuery(openid);
    return axios.get(`${ERP_API_BASE}/dialog/session/erp/${sessionId}/reviewStatus?${query}`, { timeout: 60000 }).then((r) => r.data);
  },
  closeSession(sessionId, openid) {
    if (!openid) return Promise.reject(new Error('缺少 openid'));
    return generateErpSignature(openid, ERP_SECRET).then(({ timestamp, nonce, signature }) => {
      const body = new URLSearchParams();
      body.append('openid', openid);
      body.append('timestamp', timestamp);
      body.append('nonce', nonce);
      body.append('signature', signature);
      const dhyyToken = process.env.VUE_APP_DHYY_TOKEN || '';
      return axios.post(`${ERP_API_BASE}/dialog/session/erp/${sessionId}/close`, body, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...(dhyyToken ? { dhyytoken: dhyyToken } : {}),
        },
        timeout: 60000,
      }).then((r) => r.data);
    });
  },
  /**
   * ERP 对话统一收图接口（参考 erp-upload-image-api.md）
   * POST /dialog/session/erp/uploadImage
   * 表单字段：file, openid, sessionId, sourceStep, cardSide, ocrResult, timestamp, nonce, signature
   * 后端会：1) 上传 OSS 2) 调腾讯 OCR 3) 推进 currentStep；前端不再单独 OCR 或发 WebSocket。
   * @param {Object} params {file, openid, sessionId, sourceStep, cardSide, ocrResult}
   */
  uploadErpImage(params) {
    if (!params || !params.file) {
      return Promise.reject(new Error('缺少 file'));
    }
    if (!params.openid) {
      return Promise.reject(new Error('缺少 openid'));
    }
    if (!params.sessionId) {
      return Promise.reject(new Error('缺少 sessionId'));
    }
    if (!params.sourceStep) {
      return Promise.reject(new Error('缺少 sourceStep'));
    }

    const fd = new FormData();
    fd.append('file', params.file);
    fd.append('openid', params.openid);
    fd.append('sessionId', params.sessionId);
    fd.append('sourceStep', params.sourceStep);
    if (params.cardSide) {
      fd.append('cardSide', params.cardSide);
    }
    if (params.ocrResult) {
      fd.append('ocrResult', typeof params.ocrResult === 'string' ? params.ocrResult : JSON.stringify(params.ocrResult));
    }
    // ERP_SECRET 已经常量化在文件顶部 (L11)
    return generateErpSignature(params.openid, ERP_SECRET).then(({ timestamp, nonce, signature }) => {
      fd.append('timestamp', timestamp);
      fd.append('nonce', nonce);
      fd.append('signature', signature);
      // 后端实际接口可能仍需 dhyytoken header；带上从环境变量取的值
      const dhyyToken = process.env.VUE_APP_DHYY_TOKEN || '';
      return axios.post(`${ERP_API_BASE}/dialog/session/erp/uploadImage`, fd, {
        // 不要手动设置 Content-Type，浏览器会自动补 multipart/form-data + boundary
        headers: {
          ...(dhyyToken ? { dhyytoken: dhyyToken } : {}),
        },
        timeout: 60000,
      }).then((r) => r.data);
    });
  },
  /**
   * ERP 上一步
   * POST /dialog/session/erp/goBack
   * 表单字段：openid, sessionId, timestamp, nonce, signature (HMAC-SHA256)
   * 成功响应 data = { currentStep, stepIndex, botReply, costMs }
   */
  goBackErp(params) {
    if (!params || !params.openid) {
      return Promise.reject(new Error('缺少 openid'));
    }
    if (!params.sessionId) {
      return Promise.reject(new Error('缺少 sessionId'));
    }
    return generateErpSignature(params.openid, ERP_SECRET).then(({ timestamp, nonce, signature }) => {
      const body = new URLSearchParams();
      body.append('openid', params.openid);
      body.append('sessionId', params.sessionId);
      body.append('timestamp', timestamp);
      body.append('nonce', nonce);
      body.append('signature', signature);
      const dhyyToken = process.env.VUE_APP_DHYY_TOKEN || '';
      return axios.post(`${ERP_API_BASE}/dialog/session/erp/goBack`, body, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...(dhyyToken ? { dhyytoken: dhyyToken } : {}),
        },
        timeout: 30000,
      }).then((r) => r.data);
    });
  },
  /**
   * ERP 图片类步骤推进（参考 erp-process-ocr-frontend-api.md §1.3）
   * POST /dialog/session/erp/processOcr
   * 表单字段：openid, sessionId, sourceStep, ocrResult(JSON), planInfo(JSON), timestamp, nonce, signature
   * 响应 data = { sourceStep, ocrResult, planCheck, botReply, currentStep, stepIndex, costMs }
   */
  processErpOcr(params) {
    if (!params || !params.openid) return Promise.reject(new Error('缺少 openid'));
    if (!params.sessionId) return Promise.reject(new Error('缺少 sessionId'));
    if (!params.sourceStep) return Promise.reject(new Error('缺少 sourceStep'));
    if (!params.ocrResult) return Promise.reject(new Error('缺少 ocrResult'));
    if (!params.planInfo) return Promise.reject(new Error('缺少 planInfo'));
    return generateErpSignature(params.openid, ERP_SECRET).then(({ timestamp, nonce, signature }) => {
      const body = new URLSearchParams();
      body.append('openid', params.openid);
      body.append('sessionId', params.sessionId);
      body.append('sourceStep', params.sourceStep);
      body.append('ocrResult', typeof params.ocrResult === 'string' ? params.ocrResult : JSON.stringify(params.ocrResult));
      body.append('planInfo', typeof params.planInfo === 'string' ? params.planInfo : JSON.stringify(params.planInfo));
      body.append('timestamp', timestamp);
      body.append('nonce', nonce);
      body.append('signature', signature);
      const dhyyToken = process.env.VUE_APP_DHYY_TOKEN || '';
      return axios.post(`${ERP_API_BASE}/dialog/session/erp/processOcr`, body, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...(dhyyToken ? { dhyytoken: dhyyToken } : {}),
        },
        timeout: 60000,
      }).then((r) => r.data);
    });
  },
  /**
   * ERP 选择类步骤提交（参考 erp-process-ocr-frontend-api.md §8）
   * POST /session/erp/submit
   * 表单字段：openid, sessionId, step (choose_date/choose_time/choose_plan), value, timestamp, nonce, signature
   */
  submitErpStep(params) {
    if (!params || !params.openid) return Promise.reject(new Error('缺少 openid'));
    if (!params.sessionId) return Promise.reject(new Error('缺少 sessionId'));
    if (!params.step) return Promise.reject(new Error('缺少 step'));
    if (params.value === undefined || params.value === null) return Promise.reject(new Error('缺少 value'));
    return generateErpSignature(params.openid, ERP_SECRET).then(({ timestamp, nonce, signature }) => {
      const body = new URLSearchParams();
      body.append('openid', params.openid);
      body.append('sessionId', params.sessionId);
      body.append('step', params.step);
      body.append('value', String(params.value));
      body.append('timestamp', timestamp);
      body.append('nonce', nonce);
      body.append('signature', signature);
      const dhyyToken = process.env.VUE_APP_DHYY_TOKEN || '';
      return axios.post(`${ERP_API_BASE}/dialog/session/erp/submit`, body, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...(dhyyToken ? { dhyytoken: dhyyToken } : {}),
        },
        timeout: 30000,
      }).then((r) => r.data);
    });
  },
};

// ===========================================================

export default {
  erpDialogApi,
};
